import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  topFansAll,
  topFanRadios,
  topFanLevels,
  topFanScoreActions,
  topFanDashboardStats,
  criteriaWeights,
  getFansByRadioId,
  getGlobalTopFans,
  getLevelByScore,
  getLevelProgress,
  getPodiumByRadio,
  formatTopFanCount,
  type TopFanEntry,
  type TopFanLevel,
} from '@/mocks/rfwTopFans';

function formatCompact(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function PodiumFanCard({ fan, position, isGlobal = false }: { fan: TopFanEntry; position: number; isGlobal?: boolean }) {
  const level = getLevelByScore(fan.totalScore);
  const progress = getLevelProgress(fan);
  const medals: Record<number, { gradient: string; label: string; border: string }> = {
    1: { gradient: 'from-amber-400 to-yellow-500', label: 'ORO', border: 'border-amber-400' },
    2: { gradient: 'from-slate-300 to-slate-500', label: 'PLATA', border: 'border-slate-300' },
    3: { gradient: 'from-orange-400 to-orange-600', label: 'BRONCE', border: 'border-orange-400' },
  };
  const medal = medals[position];

  return (
    <div className="flex flex-col items-center">
      <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${medal.gradient} text-background-50 text-[10px] font-extrabold tracking-wider mb-2`}>
        {medal.label}
      </div>
      <div className="relative mb-2">
        <img src={fan.photoUrl} alt={fan.name} className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover object-top border-2 ${medal.border}`} />
        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-r ${medal.gradient} flex items-center justify-center text-background-50 text-xs font-bold`}>
          {isGlobal ? fan.globalRank : position}
        </div>
      </div>
      <span className="text-xs font-bold text-foreground-950 text-center leading-tight">{fan.name}</span>
      <span className="text-[10px] text-foreground-500">{fan.username}</span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold mt-1.5 ${level.bgColor} ${level.textColor}`}>
        {level.emoji} {level.name}
      </span>
      <div className={`text-lg md:text-xl font-extrabold ${level.color} mt-1`}>{fan.totalScore}</div>
      <span className="text-[10px] text-foreground-400">pts</span>
      <div className="mt-2 flex flex-col gap-0.5 items-center">
        <span className="text-[10px] text-foreground-500">{fan.escuchas}h escuchadas</span>
        <span className="text-[10px] text-foreground-500">{fan.comentarios} coment.</span>
        <span className="text-[10px] text-foreground-500">{fan.participaciones} partic.</span>
      </div>
    </div>
  );
}

function FanListCard({ fan, position }: { fan: TopFanEntry; position: number }) {
  const level = getLevelByScore(fan.totalScore);
  const isEven = position % 2 === 0;

  return (
    <div className={`flex items-center gap-3 md:gap-4 px-4 md:px-6 py-3.5 ${isEven ? 'bg-background-50' : 'bg-background-50/70'}`}>
      <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
        <span className="text-xs font-bold text-foreground-500">#{position}</span>
      </div>
      <img src={fan.photoUrl} alt={fan.name} className="w-10 h-10 rounded-lg object-cover object-top shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground-950 truncate">{fan.name}</span>
          <span className="text-[10px] text-foreground-500">{fan.username}</span>
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${level.bgColor} ${level.textColor}`}>
            {level.emoji}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-headphone-line text-foreground-400"></i>{fan.escuchas}h
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-chat-1-line text-foreground-400"></i>{fan.comentarios}
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-message-2-line text-foreground-400"></i>{fan.saludos}
          </span>
          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-share-forward-line text-foreground-400"></i>{fan.compartidos}
          </span>
        </div>
        {fan.badges.length > 0 && (
          <div className="flex items-center gap-1 mt-1">
            {fan.badges.slice(0, 3).map((b, bi) => (
              <span key={bi} className="px-1.5 py-0.5 rounded-full bg-background-200 text-[8px] text-foreground-500">{b}</span>
            ))}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className={`text-sm font-bold ${level.color}`}>{fan.totalScore}</div>
        <div className="text-[10px] text-foreground-400">pts</div>
      </div>
    </div>
  );
}

function LevelProgressCard({ fan }: { fan: TopFanEntry }) {
  const progress = getLevelProgress(fan);
  return (
    <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-sm ${progress.current.emoji}`}>{progress.current.emoji}</span>
          <span className={`text-xs font-bold ${progress.current.textColor}`}>{progress.current.name}</span>
        </div>
        {progress.next && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-foreground-500">{progress.next.emoji} {progress.next.name}</span>
          </div>
        )}
      </div>
      <div className="w-full bg-background-200 rounded-full h-2 mb-1">
        <div className={`h-full rounded-full transition-all duration-500 ${progress.next ? 'bg-orange-500' : 'bg-emerald-500'}`} style={{ width: `${progress.percent}%` }}></div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-foreground-500">{fan.totalScore} / {progress.next ? progress.next.minScore : progress.current.maxScore} pts</span>
        <span className="text-[9px] font-medium text-orange-600">{progress.percent}%</span>
      </div>
    </div>
  );
}

export default function TopFansPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [selectedRadioId, setSelectedRadioId] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<'ranking' | 'niveles' | 'beneficios'>('ranking');
  const [selectedFanId, setSelectedFanId] = useState<number | null>(null);

  const fans = useMemo(() => {
    if (selectedRadioId === null) return getGlobalTopFans(30);
    return getFansByRadioId(selectedRadioId);
  }, [selectedRadioId]);

  const podium = useMemo(() => {
    if (selectedRadioId === null) {
      const global = getGlobalTopFans(3);
      return { gold: global[0], silver: global[1], bronze: global[2] };
    }
    return getPodiumByRadio(selectedRadioId);
  }, [selectedRadioId]);

  const restFans = useMemo(() => fans.filter((f, idx) => idx >= 3), [fans]);

  const selectedFan = selectedFanId ? topFansAll.find((f) => f.id === selectedFanId) : null;
  const selectedFanLevel = selectedFan ? getLevelByScore(selectedFan.totalScore) : null;
  const selectedFanProgress = selectedFan ? getLevelProgress(selectedFan) : null;

  const tabs = [
    { id: 'ranking' as const, label: 'Ranking', icon: 'ri-trophy-line' },
    { id: 'niveles' as const, label: 'Niveles', icon: 'ri-stack-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-gift-2-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-40 md:h-48 bg-gradient-to-br from-orange-500/20 via-amber-400/15 to-rose-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/30"></div>
              <div className="absolute -top-14 -right-14 w-60 h-60 rounded-full bg-orange-400/10 blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-amber-300/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center mb-3">
                  <i className="ri-star-line text-background-50 text-2xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-black text-foreground-950 mb-1">Top Fans RFW</h2>
                <p className="text-sm text-foreground-500">{topFanDashboardStats.totalTopFans} fans rankeados · {topFanDashboardStats.embajadores} Embajadores · {topFanDashboardStats.vips} VIPs</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-background-200">
              {[
                { label: 'Fans', value: topFanDashboardStats.totalTopFans, icon: 'ri-star-line', color: 'text-orange-500' },
                { label: 'Embajadores', value: topFanDashboardStats.embajadores, icon: 'ri-vip-crown-line', color: 'text-rose-500' },
                { label: 'VIP', value: topFanDashboardStats.vips, icon: 'ri-vip-diamond-line', color: 'text-violet-500' },
                { label: 'Top Fan', value: topFanDashboardStats.topFansLevel, icon: 'ri-fire-line', color: 'text-amber-500' },
                { label: 'Radios', value: topFanDashboardStats.activeRadios, icon: 'ri-radio-line', color: 'text-sky-500' },
                { label: 'Score', value: formatCompact(topFanDashboardStats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-emerald-500' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* View Tabs */}
          <div className="mt-5 flex items-center justify-center">
            <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeView === tab.id
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${tab.icon} text-[10px]`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Ranking View */}
          {activeView === 'ranking' && (
            <>
              {/* Radio Selector */}
              <div className="mt-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-radio-line text-foreground-500 text-xs"></i>
                  <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">Filtrar por Radio</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedRadioId(null)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedRadioId === null
                        ? 'bg-foreground-900 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                    }`}
                  >
                    <i className="ri-global-line"></i>
                    Global
                  </button>
                  {topFanRadios.map((radio) => (
                    <button
                      key={radio.id}
                      onClick={() => setSelectedRadioId(radio.id)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        selectedRadioId === radio.id
                          ? 'bg-foreground-900 text-background-50'
                          : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                      }`}
                    >
                      {radio.name}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        selectedRadioId === radio.id ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
                      }`}>
                        {radio.frequency}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fan count info */}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-foreground-400">
                  {fans.length} fans · {selectedRadioId === null ? 'Global' : topFanRadios.find((r) => r.id === selectedRadioId)?.name}
                </span>
              </div>

              {/* Podium */}
              {podium.gold && (
                <div className="mt-6">
                  <div className="flex items-end justify-center gap-3 md:gap-6 px-2">
                    {podium.silver && (
                      <PodiumFanCard fan={podium.silver} position={2} isGlobal={selectedRadioId === null} />
                    )}
                    <PodiumFanCard fan={podium.gold} position={1} isGlobal={selectedRadioId === null} />
                    {podium.bronze && (
                      <PodiumFanCard fan={podium.bronze} position={3} isGlobal={selectedRadioId === null} />
                    )}
                  </div>
                </div>
              )}

              {/* Criterios Score */}
              <div className="mt-8 bg-background-50 rounded-2xl p-5 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-scales-line text-orange-500"></i>
                  ¿Cómo se calcula el Score de Top Fan?
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                  {criteriaWeights.map((c) => (
                    <div key={c.key} className="text-center p-3 rounded-xl bg-background-100">
                      <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-1.5`}>
                        <i className={`${c.icon} ${c.color} text-sm`}></i>
                      </div>
                      <p className="text-[10px] font-bold text-foreground-950">{c.label}</p>
                      <p className="text-[9px] text-foreground-500">{(c.weight * 100).toFixed(0)}%</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rest of ranking */}
              {restFans.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-sm font-bold text-foreground-950">Posiciones 4 - {fans.length}</h3>
                  </div>
                  <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70">
                    {restFans.map((fan, idx) => (
                      <div key={fan.id} onClick={() => setSelectedFanId(fan.id)} className="cursor-pointer">
                        <FanListCard fan={fan} position={4 + idx} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fan Detail Modal */}
              {selectedFan && selectedFanLevel && selectedFanProgress && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40" onClick={() => setSelectedFanId(null)}>
                  <div className="bg-background-50 rounded-2xl p-6 w-[440px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-foreground-950">Perfil del Fan</h3>
                      <button onClick={() => setSelectedFanId(null)} className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                        <i className="ri-close-line text-foreground-500"></i>
                      </button>
                    </div>

                    <div className="text-center mb-4">
                      <img src={selectedFan.photoUrl} alt={selectedFan.name} className="w-20 h-20 rounded-2xl object-cover object-top mx-auto mb-3 border-2 border-background-200" />
                      <p className="text-base font-bold text-foreground-950">{selectedFan.name}</p>
                      <p className="text-xs text-foreground-500">{selectedFan.username} · {selectedFan.radioFrequency}</p>
                      <div className="flex items-center justify-center gap-2 mt-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${selectedFanLevel.bgColor} ${selectedFanLevel.textColor}`}>
                          {selectedFanLevel.emoji} {selectedFanLevel.name}
                        </span>
                        <span className="text-[10px] text-foreground-400">#{selectedFan.globalRank} Global</span>
                      </div>
                    </div>

                    <LevelProgressCard fan={selectedFan} />

                    <div className="mt-4 grid grid-cols-3 gap-3">
                      {criteriaWeights.map((c) => {
                        const val = selectedFan[c.key as keyof TopFanEntry] as number;
                        return (
                          <div key={c.key} className="text-center p-3 rounded-xl bg-background-100">
                            <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-1`}>
                              <i className={`${c.icon} ${c.color} text-sm`}></i>
                            </div>
                            <p className="text-sm font-bold text-foreground-950">{typeof val === 'number' ? val : '-'}</p>
                            <p className="text-[9px] text-foreground-500">{c.label}</p>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-background-100 text-center">
                        <p className="text-lg font-bold text-foreground-950">{selectedFan.streak}d</p>
                        <p className="text-[9px] text-foreground-500">Racha Activa</p>
                      </div>
                      <div className="p-3 rounded-xl bg-background-100 text-center">
                        <p className="text-lg font-bold text-foreground-950">{selectedFan.badges.length}</p>
                        <p className="text-[9px] text-foreground-500">Insignias</p>
                      </div>
                    </div>

                    {selectedFan.badges.length > 0 && (
                      <div className="mt-4 p-3 rounded-xl bg-background-100">
                        <p className="text-[10px] font-bold text-foreground-700 mb-2">Insignias Obtenidas</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedFan.badges.map((b, bi) => (
                            <span key={bi} className="px-2 py-1 rounded-full bg-background-200 text-[9px] text-foreground-600">{b}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Niveles View */}
          {activeView === 'niveles' && (
            <div className="mt-6 space-y-6">
              {/* Level Ladder */}
              <div className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-6">
                  <i className="ri-stack-line text-orange-500"></i>
                  Progresión de Niveles
                </h3>
                <div className="flex items-center justify-between gap-2">
                  {topFanLevels.map((level, idx) => (
                    <div key={level.id} className="flex-1 flex flex-col items-center gap-1.5">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${level.bgColor} border-2 ${level.borderColor}`}>
                        <span className="text-lg">{level.emoji}</span>
                      </div>
                      <p className={`text-[10px] font-bold ${level.textColor}`}>{level.name}</p>
                      <p className="text-[8px] text-foreground-400">{level.minScore}-{level.maxScore}</p>
                      {idx < topFanLevels.length - 1 && (
                        <div className="w-full h-0.5 rounded-full bg-background-200 mt-1"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Level Detail Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {topFanLevels.map((level) => (
                  <div key={level.id} className={`p-5 rounded-2xl border transition-all duration-200 bg-background-50 border-background-200/70`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${level.bgColor}`}>
                        <span className="text-lg">{level.emoji}</span>
                      </div>
                      <div>
                        <p className={`text-xs font-bold ${level.textColor}`}>{level.name}</p>
                        <p className="text-[9px] text-foreground-500">{level.minScore} - {level.maxScore} pts</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-foreground-600 leading-relaxed mb-3">{level.description}</p>
                    <p className="text-[9px] font-bold text-foreground-500 mb-2">Beneficios:</p>
                    <div className="space-y-1">
                      {level.benefits.map((b, bi) => (
                        <div key={bi} className="flex items-center gap-1.5">
                          <i className={`${level.benefitIcons[bi] || 'ri-check-line'} text-[10px] ${level.textColor}`}></i>
                          <span className="text-[10px] text-foreground-600">{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Beneficios View */}
          {activeView === 'beneficios' && (
            <div className="mt-6 space-y-6">
              {/* Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: 'Insignias', desc: 'Badges visuales que muestran tu nivel y logros en el perfil y el chat en vivo. Visible para toda la comunidad.', icon: 'ri-award-line', levels: 'Fan, Top Fan, VIP, Embajador', color: 'bg-amber-100', textColor: 'text-amber-700', iconColor: 'text-amber-600' },
                  { title: 'Reconocimiento', desc: 'Menciones al aire durante los programas, destacado en rankings públicos y redes sociales de RFW.', icon: 'ri-megaphone-line', levels: 'Top Fan, VIP, Embajador', color: 'bg-orange-100', textColor: 'text-orange-700', iconColor: 'text-orange-600' },
                  { title: 'Acceso Anticipado', desc: 'Entrada prioritaria a eventos exclusivos, acceso temprano a nuevos concursos y contenido premium antes que nadie.', icon: 'ri-ticket-2-line', levels: 'Top Fan, VIP, Embajador', color: 'bg-violet-100', textColor: 'text-violet-700', iconColor: 'text-violet-600' },
                  { title: 'Premios', desc: 'Regalos físicos y digitales: merch oficial RFW, entradas a conciertos, suscripciones premium gratuitas.', icon: 'ri-gift-2-line', levels: 'VIP, Embajador', color: 'bg-rose-100', textColor: 'text-rose-700', iconColor: 'text-rose-600' },
                ].map((benefit) => (
                  <div key={benefit.title} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl ${benefit.color} flex items-center justify-center shrink-0`}>
                        <i className={`${benefit.icon} ${benefit.iconColor} text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 mb-1">{benefit.title}</h4>
                        <p className="text-[11px] text-foreground-500 leading-relaxed mb-2">{benefit.desc}</p>
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-medium ${benefit.color} ${benefit.textColor}`}>
                          {benefit.levels}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Score Actions */}
              <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-flashlight-line text-orange-500"></i>
                  Acciones que Suman Puntos de Top Fan
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {topFanScoreActions.map((action) => (
                    <div key={action.key} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl ${action.bgColor} border border-transparent`}>
                      <i className={`${action.icon} ${action.color} text-base`}></i>
                      <p className="text-sm font-extrabold text-foreground-950">+{action.points}</p>
                      <p className="text-[9px] text-foreground-600 font-medium text-center">{action.label}</p>
                      <p className="text-[8px] text-foreground-400 text-center">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center border border-background-200/70">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center">
              <i className="ri-rocket-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">¿Querés ser Top Fan?</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Escuchá, comentá, saludá, pedí canciones, compartí y participá. Cada acción te acerca al siguiente nivel. Los mejores fans reciben reconocimiento, beneficios y premios.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/gamificacion"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line mr-1.5"></i>
                Ver Gamificación
              </Link>
              <Link
                to="/insignias"
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-award-line mr-1.5"></i>
                Insignias
              </Link>
            </div>
          </div>

          {/* Cross-links */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/oyente" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <i className="ri-user-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Tu Perfil</h4>
                  <p className="text-xs text-foreground-500">Estadísticas y actividad</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/niveles" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                  <i className="ri-stack-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Niveles</h4>
                  <p className="text-xs text-foreground-500">Sistema de progresión</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/compartidos" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <i className="ri-share-forward-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Compartidos</h4>
                  <p className="text-xs text-foreground-500">Sistema de referidos</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla Final */}
          <div className="mt-8 p-6 rounded-2xl bg-orange-100/70 border border-orange-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-star-line text-lg text-white"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              Cada radio debe tener su Top Fans.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              No existe una sola comunidad RFW — cada radio construye la suya. Los Top Fans son el corazón de cada comunidad. Identificarlos, reconocerlos y premiarlos es la base del crecimiento orgánico de RFW.
            </p>
          </div>

        </div>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}